/**
 * UserDashboardAllObjectiveDrawer
 *
 * Bottom drawer showing a user's Objectives, Key Results, Initiatives, and Milestones.
 * Loads status lists, employees, and departments for filters/context and fetches profile
 * details for the selected user. Composes header, sidebar, and main content sections.
 */
import React, { useEffect, useMemo, useState } from "react";
import "./user-dashboard-all-objective-drawer.scss";
import UserDashboardAllObjectiveDrawerHeader from "./user-dashboard-all-objective-drawer-header/user-dashboard-all-objective-drawer-header";
import UserDashboardAllObjectiveDrawerSideBar from "./user-dashboard-all-objective-drawer-side-bar/user-dashboard-all-objective-drawer-side-bar";
import UserDashboardAllObjectiveDrawerMainContent from "./user-dashboard-all-objective-drawer-main-content/user-dashboard-all-objective-drawer-main-content";
import { useSetLoading } from "../../../common/hooks/appearance";
import {
  getAllDepartments,
  getAllEmployees,
  getAllStatus,
  getAllStatusForInitiatives,
  getAllStatusForKeyResults,
} from "../../../api/dashboard";
import { getEmployeeObjectives } from "../../../api/user-dashboard";
import { getEmployee } from "../../../api/profile";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";

const UserDashboardAllObjectiveDrawer: any = (props: any) => {
  const { setIsShowAllObjectiveDrawer, approverId } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [statusList, setStatusList] = useState<any[]>([]);
  const [statusListKeyResults, setStatusListKeyResults] = useState<any[]>([]);
  const [statusListInitiatives, setStatusListInitiatives] = useState<any[]>([]);
  const [employeesList, setEmployeesList] = useState<any[]>([]);
  const [departmentsList, setDepartmentsList] = useState<any[]>([]);
  const [profileData, setProfileData] = useState<any>();
  const [employeeId, setEmployeeId] = useState<number | null>(approverId);

  const fetchAllStatus = async () => {
    setLoading(true);
    try {
      const response = await getAllStatus();
      setStatusList(response?.data?.data || []);
      localStorage.setItem(
        "statusList",
        JSON.stringify(response?.data?.data || []),
      );
      setTimeout(() => setLoading(false), 0);
    } catch (error: any) {
      console.error(error);

      setLoading(false);
    }
  };
  const fetchAllStatusForKeyResults = async () => {
    setLoading(true);
    try {
      const response = await getAllStatusForKeyResults();
      setStatusListKeyResults(response?.data?.data || []);

      setTimeout(() => setLoading(false), 0);
    } catch (error: any) {
      console.error(error);
      setLoading(false);
    }
  };
  const fetchAllStatusForInitiatives = async () => {
    setLoading(true);
    try {
      const response = await getAllStatusForInitiatives();
      setStatusListInitiatives(response?.data?.data || []);

      setTimeout(() => setLoading(false), 0);
    } catch (error: any) {
      console.error(error);
      setLoading(false);
    }
  };
  const fetchAllEmployees = async () => {
    setLoading(true);
    try {
      const response = await getAllEmployees({});
      setEmployeesList(response?.data?.data || []);
      localStorage.setItem(
        "employeesList",
        JSON.stringify(response?.data?.data || []),
      );
      setTimeout(() => setLoading(false), 0);
    } catch (error: any) {
      console.error(error);
      setLoading(false);
    }
  };
  const fetchAllDepartments = async () => {
    setLoading(true);
    try {
      const response = await getAllDepartments();
      setDepartmentsList(response?.data?.data || []);
      localStorage.setItem(
        "departmentsList",
        JSON.stringify(response?.data?.data || []),
      );
      setTimeout(() => setLoading(false), 0);
    } catch (error: any) {
      console.error(error);

      setLoading(false);
    }
  };
  const fetchObjectives = async (employeeId: number) => {
    setLoading(true);
    try {
      await getEmployeeObjectives(employeeId);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  const getInitials = (name: string) => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts.at(-1)?.[0])?.toUpperCase();
  };
  const fetchUserDetails = async (employeeId: number) => {
    setLoading(true);
    try {
      const response = await getEmployee(employeeId);
      const data = response?.data?.data;

      // Prepare profileData
      const profile = {
        name: data?.employeeFullName || "-",
        role: data?.roles?.length
          ? data?.roles?.map((item: any) => item.name).join(", ")
          : " - ",
        email: data?.emailId || "-",
        image: data?.employeeImage || null,
        departments:
          data?.departmentName && data?.departmentId
            ? [{ name: data.departmentName, id: data.departmentId }]
            : [{ name: "Sales", id: 1 }],

        initials: getInitials(data?.employeeFullName || "-"),

        // Reporting To
        reportingTo: data?.reportingTo
          ? {
              name: data.reportingTo.employeeFullName,
              role: data.reportingTo.roleName,
              image: data.reportingTo.employeeImage || null,
              employeeId: data.reportingTo.employeeId,
              initials: getInitials(data?.employeeFullName || "-"),
            }
          : null,

        // Department Members
        departmentMembers:
          data?.departmentEmployeeResponse?.employees?.map((emp: any) => ({
            name: emp.employeeFullName,
            role: emp.roleName,
            image: emp.employeeImage || null,
            employeeId: emp.employeeId,
            initials: getInitials(emp.employeeFullName || "-"),
          })) || [],
      };

      setProfileData(profile);
      setLoading(false);
    } catch (error: any) {
      console.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllStatus();
    fetchAllStatusForKeyResults();
    fetchAllStatusForInitiatives();
    fetchAllEmployees();
    fetchAllDepartments();
    if (employeeId) {
      fetchUserDetails(employeeId);
    }
  }, [employeeId]);
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
  return (
    <OverlayScrollbarsComponent
      className="user-dashboard-all-objective-drawer"
      options={{
        scrollbars: {
          theme: "os-theme-dark",
          autoHide: "scroll",
          autoHideDelay: 400,
        },
      }}
    >
      <div className="user-dashboard-all-objective-drawer-content">
        <UserDashboardAllObjectiveDrawerHeader
          setIsShowAllObjectiveDrawer={setIsShowAllObjectiveDrawer}
        />
        <div className="user-dashboard-all-objective-drawer-content-body">
          <div className="user-dashboard-all-objective-drawer-content-body-section">
            <div className="user-dashboard-all-objective-drawer-content-body-section-left">
              <UserDashboardAllObjectiveDrawerSideBar
                setIsShowAllObjectiveDrawer={setIsShowAllObjectiveDrawer}
                employeeId={employeeId}
                profileData={profileData}
                onViewEmployee={(newEmployeeId: number) =>
                  setEmployeeId(newEmployeeId)
                }
              />
            </div>
            <div className="user-dashboard-all-objective-drawer-content-body-section-right">
              <UserDashboardAllObjectiveDrawerMainContent
                approverId={employeeId}
                handleGetFunction={fetchObjectives}
                employeesList={employeesList}
                statusListKeyResults={statusListKeyResults}
                statusListObjectives={statusList}
                statusListInitiatives={statusListInitiatives}
                departmentsList={departmentsList}
                setAlertMessage={() => {}}
                setAlertType={() => {}}
                setAlertOpen={() => {}}
                setLoading={setLoading}
                setEmployeeId={setEmployeeId}
              />
            </div>
          </div>
        </div>
      </div>
    </OverlayScrollbarsComponent>
  );
};

export default UserDashboardAllObjectiveDrawer;
